import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'

import { escapeRegExp } from '../../utils'

// Helper function to format number with thousand separators
const formatWithThousandSeparators = (value: string): string => {
  if (!value) return ''

  // Split by decimal point
  const parts = value.split('.')
  const integerPart = parts[0]
  const decimalPart = parts[1]

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  // Combine with decimal part if exists
  if (decimalPart !== undefined) {
    return `${formattedInteger}.${decimalPart}`
  }

  return formattedInteger
}

const StyledInput = styled.input<{ error?: boolean; fontSize?: string; align?: string }>`
  color: ${({ error, theme }) => (error ? theme.accentFailure : theme.textPrimary)};
  width: 0;
  position: relative;
  font-weight: 400;
  outline: none;
  border: none;
  flex: 1 1 auto;
  background-color: transparent;
  font-size: ${({ fontSize }) => fontSize ?? '28px'};
  text-align: ${({ align }) => align && align};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0px;
  -webkit-appearance: textfield;
  text-align: right;

  ::-webkit-search-decoration {
    -webkit-appearance: none;
  }

  [type='number'] {
    -moz-appearance: textfield;
  }

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  ::placeholder {
    color: ${({ theme }) => theme.textTertiary};
  }
`

const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`) // match escaped "." characters via in a non-capturing group

export const Input = React.memo(function InnerInput({
  value,
  onUserInput,
  placeholder,
  prependSymbol,
  maxDecimals,
  ...rest
}: {
  value: string | number
  onUserInput: (input: string) => void
  error?: boolean
  fontSize?: string
  align?: 'right' | 'left'
  prependSymbol?: string
  maxDecimals?: number
} & Omit<React.HTMLProps<HTMLInputElement>, 'ref' | 'onChange' | 'as'>) {
  const [localValue, setLocalValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  // Update local value when prop value changes (only when not focused)
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value.toString())
    }
  }, [value, isFocused])

  const enforcer = (nextUserInput: string) => {
    if (nextUserInput === '' || inputRegex.test(escapeRegExp(nextUserInput))) {
      // Check decimal places limit if maxDecimals is provided
      if (maxDecimals !== undefined && nextUserInput.includes('.')) {
        const parts = nextUserInput.split('.')
        if (parts[1] && parts[1].length > maxDecimals) {
          // Truncate to maxDecimals
          const truncatedValue = parts[0] + '.' + parts[1].slice(0, maxDecimals)
          setLocalValue(truncatedValue)
          onUserInput(truncatedValue)
          return
        }
      }

      setLocalValue(nextUserInput)
      onUserInput(nextUserInput)
    }
  }

  // Format the display value with thousand separators (always show separators)
  const displayValue = formatWithThousandSeparators(localValue)

  return (
    <StyledInput
      {...rest}
      value={prependSymbol && displayValue ? prependSymbol + displayValue : displayValue}
      onFocus={(event) => {
        setIsFocused(true)
        rest.onFocus?.(event)
      }}
      onBlur={(event) => {
        setIsFocused(false)
        rest.onBlur?.(event)
      }}
      onChange={(event) => {
        let inputValue = event.target.value

        if (prependSymbol) {
          // cut off prepended symbol
          const value = inputValue.toString()
          inputValue = value.includes(prependSymbol) ? value.slice(1, value.length + 1) : value
        }

        // Remove thousand separators first, then replace any remaining commas with periods
        const cleanValue = inputValue.replace(/,/g, '')
        enforcer(cleanValue)
      }}
      // universal input options
      inputMode="decimal"
      autoComplete="off"
      autoCorrect="off"
      // text-specific options
      type="text"
      pattern="^[0-9]*[.,]?[0-9]*$"
      placeholder={placeholder || '0'}
      minLength={1}
      maxLength={79}
      spellCheck="false"
    />
  )
})

export default Input

// const inputRegex = RegExp(`^\\d*(?:\\\\[.])?\\d*$`) // match escaped "." characters via in a non-capturing group
